/*
  legalContent.js
  ─────────────────────────────────────────────────────────────
  Structured content for Restropilots' Legal, Privacy, Data
  Protection, Security & Confidentiality Document Pack (v1.0,
  14 July 2026), extracted from
  Restropilots_Legal_Compliance_Document_Pack.docx

  IMPORTANT: This is a business draft. It should be reviewed by
  qualified Indian legal counsel before contractual reliance or
  publication — see the note rendered at the top of the reader.

  Each document is: { id, title, meta, sections: [{ heading, paragraphs?, bullets? }] }
*/

export const LEGAL_VERSION = "1.0";
export const LEGAL_EFFECTIVE_DATE = "14 July 2026";

export const legalDocuments = [
  {
    id: "privacy",
    title: "Privacy Policy",
    meta: `Effective Date: ${LEGAL_EFFECTIVE_DATE} · Version ${LEGAL_VERSION}`,
    sections: [
      {
        heading: "1. Introduction",
        paragraphs: [
          `Restropilots ("Restropilots", "we", "us" or "our") provides software-as-a-service and digital technology solutions intended to enhance the experience and operations of restaurants and their customers. Our services may include QR digital menus, restaurant storefronts, order and table management, customer relationship management, feedback, analytics, marketing functionality, FoodFinder, online payments, subscription services, and AI-enabled features.`,
          `This Privacy Policy explains how we collect, use, disclose, retain, protect and otherwise process personal data when individuals visit our websites, create or use accounts, interact with restaurant services powered by Restropilots, use FoodFinder, communicate with us, make payments, or otherwise use our services.`,
          `Our primary website is restropilots.com. Privacy and grievance requests may be sent to director@restropilots.co.in. Security matters may be sent to security@restropilots.co.in.`,
        ],
      },
      {
        heading: "2. Scope and Roles",
        paragraphs: [
          `This Policy applies to restaurant owners, restaurant personnel, individual customers, website visitors and other users of Restropilots services.`,
          `Depending on the processing activity, Restropilots may determine the purposes and means of processing for its own account administration, security, billing, service operation, marketing and legal compliance activities. In other contexts, Restropilots may process information on behalf of a restaurant customer in accordance with the restaurant's instructions and the applicable agreement. The precise allocation of responsibilities may be further described in a Data Processing Addendum.`,
        ],
      },
      {
        heading: "3.1 Restaurant and Business Information",
        bullets: [
          "Business and restaurant name, owner or representative name, telephone number, email address and business address.",
          "Business verification and compliance information, which may include GSTIN, FSSAI information, PAN information and identity or business documentation submitted for verification or onboarding.",
          "Banking, settlement or UPI-related information where required for enabled payment or settlement functionality.",
          "Restaurant logos, photographs, menus, prices, product information and other content uploaded to the platform.",
          "Subscription, billing, transaction and account-administration information.",
        ],
      },
      {
        heading: "3.2 Individual Customer Information",
        bullets: [
          "Name, telephone number and email address.",
          "Google sign-in information made available to us through the authentication flow, subject to the user's Google account permissions.",
          "Order history, table number, feedback, reviews and interactions with participating restaurants.",
          "Payment-related transaction information, such as payment status and transaction identifiers. Restropilots states that it does not store card numbers, CVV or UPI IDs.",
          "Location information where a location-enabled feature is used. The nature and precision of location information may depend on the relevant feature and device permissions.",
        ],
      },
      {
        heading: "3.3 Account, Authentication and Technical Information",
        bullets: [
          "Restaurant authentication information, including email/password credentials, OTP-related information and security events.",
          "Cookies and similar technologies used for login, authentication, session management and essential functionality.",
          "Application, security, login and audit logs generated through use of the services.",
          "Information reasonably necessary to detect abuse, secure accounts, troubleshoot errors and maintain the service.",
        ],
      },
      {
        heading: "3.4 Information We Do Not Intend to Store",
        paragraphs: [
          `Based on the current service design, Restropilots does not intend to store full card numbers, CVV values or UPI IDs. Payment providers may process payment credentials directly under their own terms and privacy practices.`,
        ],
      },
      {
        heading: "4. How We Collect Information",
        bullets: [
          "Directly from restaurant owners, representatives and users.",
          "Directly from individual customers when they sign in, order, provide feedback or use Restropilots services.",
          "Through Google sign-in and other authentication services selected by the user.",
          "Automatically through essential cookies, authentication systems, application logs and security systems.",
          "From payment providers in the form of transaction status, identifiers and related payment information.",
          "From restaurants where information is lawfully provided to us for delivery of the contracted service.",
        ],
      },
      {
        heading: "5. Purposes of Processing",
        bullets: [
          "Provide, operate, maintain and improve the Restropilots platform and its features.",
          "Create, authenticate and secure user and restaurant accounts.",
          "Enable QR ordering, table management, order management, restaurant storefronts, FoodFinder and related services.",
          "Process subscriptions, payment status, transaction records, refunds and related support.",
          "Provide restaurants with analytics and business insights intended to help them understand operations, customer patterns and business performance.",
          "Provide customer support and respond to requests.",
          "Send service, transactional and, where legally permitted, promotional communications.",
          "Detect fraud, abuse, security incidents and violations of our agreements.",
          "Develop and improve platform functionality, analytics and AI-enabled features, subject to applicable law and appropriate safeguards.",
          "Comply with legal, tax, accounting, regulatory and law-enforcement obligations.",
          "Establish, exercise or defend legal claims.",
        ],
      },
      {
        heading: "6. Marketing Communications",
        paragraphs: [
          `Restropilots may send marketing communications to restaurant customers or other users where permitted by applicable law and where an appropriate basis for such communication exists. Users should be provided with a practical method to opt out of promotional communications. Service-related and security communications may still be sent where necessary to operate or protect an account.`,
          `Restaurants using future marketing functionality are responsible for ensuring that their use of customer contact information and promotional communications complies with applicable law and the permissions or consents required for their intended communication channel.`,
        ],
      },
      {
        heading: "7. Payments",
        paragraphs: [
          `Restropilots uses Cashfree for enabled payment and subscription functionality. Payment credentials may be collected and processed directly by the payment provider. Restropilots may receive and retain payment status, transaction identifiers and related records required to operate the service, reconcile payments, support refunds, prevent fraud and meet legal obligations.`,
          `The exact settlement flow for restaurant food-order payments may depend on the configured Cashfree arrangement. The applicable restaurant agreement and payment flow govern whether and how amounts are settled to a restaurant.`,
        ],
      },
      {
        heading: "8. Cookies",
        paragraphs: [
          `Restropilots uses cookies or similar technologies for essential login, authentication and session functionality. Based on the current information supplied, Restropilots does not currently use Google Analytics or advertising pixels. If non-essential analytics or advertising technologies are introduced, this Policy and any consent mechanism should be updated as required.`,
        ],
      },
      {
        heading: "9. Sharing and Service Providers",
        paragraphs: [
          `We may disclose information to service providers and subprocessors where reasonably necessary to operate the services. Current or anticipated categories include hosting, database, cloud infrastructure, file storage, authentication, email, SMS, payment, monitoring and logging providers.`,
          `Services identified in the current technical environment include Netlify, Render, MongoDB, Amazon Web Services (including S3), Google services and APIs, Cashfree, GoDaddy-related email services, Grafana and Prometheus or related monitoring infrastructure. The exact provider configuration and hosting location should be maintained in an up-to-date internal vendor register and, where appropriate, a subprocessor schedule.`,
          `We may also disclose information where required by law, to protect rights and security, in connection with a corporate transaction, or with the relevant person's authorization.`,
        ],
      },
      {
        heading: "10. Data Retention",
        paragraphs: [
          `We retain personal data for no longer than reasonably necessary for the purposes for which it was collected, to provide the services, maintain security, resolve disputes and comply with legal, accounting and regulatory requirements.`,
          `A restaurant whose subscription ends is currently intended to receive a seven-day period to access or export available data. After the applicable service and transition period, data should be deleted, anonymized or retained only where a legitimate operational or legal reason requires continued retention. Transaction, tax, accounting and legal records may be retained for applicable statutory periods. Residual data may remain in backups until overwritten or removed through the applicable backup-retention cycle.`,
          `Restropilots should maintain and periodically review a documented retention schedule. Indefinite retention should not be used as a default where information is no longer required.`,
        ],
      },
      {
        heading: "11. Data Security",
        paragraphs: [
          `Restropilots uses administrative, technical and organizational measures intended to protect information. Current controls reported by Restropilots include HTTPS/TLS, password hashing, MFA for cloud administrative accounts, separation of development and production environments, restricted production database access, encryption at rest, automated backups, private source-code repositories, code review, dependency and security scanning, application and audit logging, rate limiting, brute-force protections, vulnerability scanning, access restriction by job requirement and access revocation when access is no longer required.`,
          `No security measure can guarantee absolute security. Restropilots continues to improve its security program, including incident-response documentation, backup restoration testing and independent security testing.`,
        ],
      },
      {
        heading: "12. Your Choices and Rights",
        paragraphs: [
          `Subject to applicable law and the nature of our relationship with you, you may request access to information about your personal data, correction of inaccurate information, deletion where applicable, withdrawal of consent where processing is based on consent, or grievance redressal. Certain requests may be subject to legal exceptions, identity verification, retention obligations or the role of the relevant restaurant.`,
          `Requests may be sent to director@restropilots.co.in. Where Restropilots processes data on behalf of a restaurant, we may direct or coordinate the request with that restaurant as appropriate.`,
        ],
      },
      {
        heading: "13. Account and Data Deletion",
        paragraphs: [
          `Individual customers may request deletion of their personal data, subject to applicable legal and operational requirements. Restaurant self-service account deletion is under development. Until automated deletion is available, eligible deletion requests should be handled through the designated privacy or grievance contact.`,
        ],
      },
      {
        heading: "14. Children",
        paragraphs: [
          `The services are not intended to solicit personal data from children in a manner prohibited by applicable law. Where the processing of a child's personal data requires verified parental consent or other safeguards, Restropilots and participating restaurants must apply the requirements applicable to the relevant processing activity.`,
        ],
      },
      {
        heading: "15. International and Cross-Border Processing",
        paragraphs: [
          `Service providers may process or store information in locations determined by their infrastructure and contractual arrangements. Restropilots will address cross-border processing in accordance with applicable Indian law and any restrictions or requirements in force from time to time.`,
        ],
      },
      {
        heading: "16. Changes to This Policy",
        paragraphs: [
          `We may update this Privacy Policy to reflect changes in our services, technology, legal obligations or practices. Material changes may be communicated through the service, website, email or another appropriate method. The effective date at the top of the Policy indicates the current version.`,
        ],
      },
      {
        heading: "17. Contact and Grievance Redressal",
        paragraphs: [
          `Privacy and grievance contact: director@restropilots.co.in`,
          `Security contact: security@restropilots.co.in`,
          `General contact: foodfinder921@gmail.com`,
          `Registered office: OLD NO E-3-KH NO-305, NEW NO-144, East Delhi, Delhi 110096, India.`,
        ],
      },
    ],
  },

  {
    id: "terms",
    title: "Terms of Service",
    meta: `Effective Date: ${LEGAL_EFFECTIVE_DATE} · Version ${LEGAL_VERSION}`,
    sections: [
      {
        heading: "1. Agreement",
        paragraphs: [
          `These Terms of Service ("Terms") govern access to and use of the Restropilots websites, applications, software, restaurant technology services and related features (collectively, the "Services"). By creating an account, accepting an order form, purchasing a subscription or using the Services, the person or entity using the Services ("Customer" or "you") agrees to these Terms.`,
          `If you use the Services on behalf of a restaurant, company or other organization, you represent that you have authority to bind that organization.`,
        ],
      },
      {
        heading: "2. The Services",
        paragraphs: [
          `Restropilots provides software-as-a-service and technology intended to enhance restaurant and customer experiences. Depending on the selected plan and availability, features may include QR digital menus, restaurant websites or storefronts, order management, dine-in and takeaway ordering, table management, feedback, CRM functionality, analytics, FoodFinder, online payments, subscription services and AI-enabled features. Certain features may be in development, beta, limited availability or subject to separate terms.`,
          `Restropilots does not itself prepare, manufacture or serve food and is not responsible for a restaurant's food quality, safety, ingredients, allergens, menu accuracy, availability, statutory licences, tax compliance or fulfilment obligations.`,
        ],
      },
      {
        heading: "3. Accounts and Authorized Users",
        paragraphs: [
          `Customers must provide accurate information and maintain the security of their accounts. Restaurant owners may authorize additional users where the relevant functionality is available. The Customer is responsible for activity conducted through its accounts and for promptly reporting suspected unauthorized access.`,
          `Restropilots may use email/password authentication, OTP and other authentication methods for restaurant accounts and Google sign-in or other authentication methods for individual customers.`,
        ],
      },
      {
        heading: "4. Restaurant Responsibilities",
        bullets: [
          "Maintain accurate menus, prices, taxes, availability, business details and customer-facing information.",
          "Maintain all licences, registrations and approvals required to operate the restaurant, including food-safety and tax registrations where applicable.",
          "Ensure that uploaded content and data are lawful and that the restaurant has the rights and permissions required to provide them to Restropilots.",
          "Handle food preparation, quality, safety, allergen information, fulfilment, customer service and restaurant-specific refund obligations.",
          "Use customer information and communication features in accordance with applicable privacy, marketing and communications law.",
          "Maintain the confidentiality of account credentials and manage authorized users.",
        ],
      },
      {
        heading: "5. Verification Information",
        paragraphs: [
          `Restropilots may request business, tax, food-safety, identity, PAN, banking or other information for onboarding, verification, payment or compliance purposes. Current verification may include manual review and may later include automated verification. Submission or review of information does not constitute a guarantee by Restropilots that a restaurant is compliant with all applicable laws.`,
        ],
      },
      {
        heading: "6. Subscriptions, Fees and Taxes",
        paragraphs: [
          `Restaurant subscriptions are currently offered on annual billing periods unless otherwise specified in an order form or plan description. Subscriptions do not automatically renew unless Restropilots introduces and clearly discloses an automatic-renewal arrangement.`,
          `Fees, applicable taxes and payment terms will be shown at purchase or in the applicable order form. Restropilots may suspend or restrict access for overdue or unpaid amounts after reasonable notice where appropriate.`,
          `Restropilots may change pricing for future subscription periods by providing reasonable notice. A price change will not retroactively change fees already paid for a fixed subscription period unless agreed by the parties.`,
        ],
      },
      {
        heading: "7. Free Trials",
        paragraphs: [
          `Restropilots may offer free trials for a period specified at signup or in the applicable offer. Trial features may be limited and may be modified or withdrawn. Unless expressly stated otherwise, a trial does not create a commitment to provide the Services indefinitely or at no charge.`,
        ],
      },
      {
        heading: "8. Cancellation and Refunds",
        paragraphs: [
          `A restaurant may request cancellation or a refund through the support process made available by Restropilots. Eligibility for a subscription refund will depend on the applicable plan, reason for the request, usage of the Services, payment-provider rules and any specific terms shown at purchase. Nothing in these Terms limits non-waivable rights under applicable law.`,
          `Food-order refunds are primarily connected to the underlying restaurant transaction. Depending on the payment flow and circumstances, the restaurant, Restropilots and/or the payment provider may participate in processing a refund. The restaurant remains responsible for the underlying food, fulfilment and restaurant-specific customer dispute unless Restropilots expressly assumes responsibility in writing.`,
        ],
      },
      {
        heading: "9. Payments",
        paragraphs: [
          `Cashfree is used for enabled online payments and subscription payments. Restropilots does not intend to store full card numbers, CVV values or UPI IDs. Payment providers may process payment credentials under their own terms.`,
          `Settlement of restaurant food-order payments will follow the configured payment arrangement. Restropilots may maintain transaction identifiers, payment status and records needed for reconciliation, support, refunds, fraud prevention and compliance.`,
        ],
      },
      {
        heading: "10. Customer and Restaurant Data",
        paragraphs: [
          `As between Restropilots and a restaurant Customer, the Customer retains its rights in data and content that it lawfully provides to the Services ("Customer Data"), subject to the rights of individual data subjects and third parties.`,
          `The Customer grants Restropilots a limited, non-exclusive right to host, copy, transmit, process, display and otherwise use Customer Data as reasonably necessary to provide, secure, support and improve the Services, comply with law and perform the agreement.`,
          `Restropilots may create aggregated or de-identified information for analytics, service improvement and business insights where such information is handled in accordance with applicable law and is not used to identify an individual.`,
        ],
      },
      {
        heading: "11. Data Protection",
        paragraphs: [
          `Each party will comply with applicable data-protection obligations relevant to its activities. Where Restropilots processes personal data on behalf of a Customer, the Data Processing Addendum may apply. Customers must provide lawful instructions and must not use the Services to process data they are not authorized to collect or use.`,
        ],
      },
      {
        heading: "12. Intellectual Property",
        paragraphs: [
          `Restropilots and its licensors retain all rights, title and interest in the Services, software, source code, object code, interfaces, designs, documentation, algorithms, platform architecture, trademarks, logos, know-how and other technology, excluding Customer Data and third-party materials.`,
          `Except for the limited right to use the Services during the applicable subscription, no intellectual-property rights are transferred to the Customer.`,
        ],
      },
      {
        heading: "13. Feedback",
        paragraphs: [
          `If a Customer voluntarily provides suggestions or feedback about the Services, Restropilots may use that feedback to improve its products without restriction or payment, provided that Restropilots does not thereby acquire ownership of the Customer's confidential information.`,
        ],
      },
      {
        heading: "14. Acceptable Use",
        paragraphs: [
          `Users must not misuse the Services. Prohibited conduct includes unauthorized access, credential abuse, unlawful processing of personal data, malware distribution, security testing without authorization, interference with service operation, fraudulent activity, infringement of third-party rights, unlawful content, or attempts to circumvent technical restrictions.`,
          `Users must not reverse engineer, decompile or attempt to derive source code except to the limited extent such restriction is prohibited by applicable law.`,
        ],
      },
      {
        heading: "15. Suspension",
        paragraphs: [
          `Restropilots may suspend or restrict access where reasonably necessary due to non-payment, a material breach, suspected fraud, unlawful activity, a security risk, harm to the Services or third parties, or a legal requirement. Where practicable, Restropilots will provide notice and an opportunity to remedy the issue.`,
        ],
      },
      {
        heading: "16. Termination and Data Export",
        paragraphs: [
          `Upon expiry or termination, the Customer's right to use the paid Services ends unless otherwise agreed. Restropilots currently intends to provide a seven-day period for eligible restaurant Customers to access or export available data. After that period, data may be deleted, anonymized or retained where required for legal, security, payment, dispute or backup purposes.`,
          `Provisions that by their nature should survive termination, including intellectual property, confidentiality, accrued payment obligations, disclaimers, limitations of liability and dispute provisions, will survive.`,
        ],
      },
      {
        heading: "17. Availability and Changes",
        paragraphs: [
          `Restropilots does not currently provide a contractual uptime service-level agreement unless expressly agreed in writing. The Services may be unavailable due to maintenance, updates, third-party infrastructure failures, emergencies, security events or circumstances outside reasonable control.`,
          `Restropilots may modify or discontinue features as the platform evolves. Where a change materially reduces a paid core service during a current fixed subscription period, Restropilots will seek to provide reasonable notice where practicable.`,
        ],
      },
      {
        heading: "18. Third-Party Services",
        paragraphs: [
          `The Services may depend on or integrate with third-party services, including payment, hosting, database, cloud, authentication, email, SMS and monitoring providers. Restropilots is not responsible for third-party services outside its reasonable control, although it will use reasonable efforts to manage providers relevant to the operation of its Services.`,
        ],
      },
      {
        heading: "19. Disclaimers",
        paragraphs: [
          `To the maximum extent permitted by law, the Services are provided on an "as is" and "as available" basis. Restropilots does not warrant that the Services will be uninterrupted, error-free or suitable for every business purpose. Analytics and AI-enabled outputs are informational tools and should not be treated as guaranteed business, financial, legal, tax, food-safety or other professional advice.`,
        ],
      },
      {
        heading: "20. Limitation of Liability",
        paragraphs: [
          `To the maximum extent permitted by applicable law, neither party will be liable for indirect, incidental, special, exemplary, punitive or consequential damages, or for loss of profits, revenue, goodwill or business opportunity, arising from the agreement, except to the extent such exclusion is prohibited by law.`,
          `Subject to liabilities that cannot legally be limited and any express exceptions agreed in writing, Restropilots' aggregate liability arising out of or relating to the Services will not exceed the fees paid or payable by the affected Customer to Restropilots during the twelve months immediately preceding the event giving rise to the claim.`,
          `This limitation is intended for business-to-business SaaS arrangements and should be reviewed by legal counsel for the final commercial model and any consumer-facing services.`,
        ],
      },
      {
        heading: "21. Indemnity",
        paragraphs: [
          `To the extent permitted by law, a restaurant Customer will indemnify and hold Restropilots harmless from third-party claims arising from the Customer's unlawful content or data, food preparation or fulfilment, restaurant operations, violation of applicable law, infringement of third-party rights, or misuse of the Services, except to the extent caused by Restropilots' own breach or wrongful conduct.`,
        ],
      },
      {
        heading: "22. Changes to Terms",
        paragraphs: [
          `Restropilots may update these Terms. Material changes will be communicated through the website, Services, email or another reasonable method. Changes will take effect on the stated effective date, subject to applicable law and any fixed contractual commitments.`,
        ],
      },
      {
        heading: "23. Governing Law and Dispute Resolution",
        paragraphs: [
          `These Terms are governed by the laws of India, without regard to conflict-of-law principles.`,
          `The parties will first attempt in good faith to resolve a dispute through negotiation. If the dispute is not resolved within thirty days after written notice of the dispute, it may be referred to arbitration in accordance with the Arbitration and Conciliation Act, 1996, as amended. Unless otherwise agreed, the arbitration will be conducted by a sole arbitrator mutually appointed by the parties, in the English language, with New Delhi, India as the seat and venue of arbitration.`,
          `Courts of competent jurisdiction at New Delhi, India will have jurisdiction for matters not capable of arbitration and for permitted interim or enforcement relief.`,
        ],
      },
      {
        heading: "24. General",
        paragraphs: [
          `Neither party may assign the agreement in a manner prohibited by law. Restropilots may assign the agreement in connection with a merger, reorganization, financing or sale of substantially all relevant assets, subject to applicable law. Failure to enforce a provision is not a waiver. If a provision is unenforceable, the remaining provisions remain effective.`,
        ],
      },
      {
        heading: "25. Contact",
        paragraphs: [
          `General contact: foodfinder921@gmail.com`,
          `Privacy and grievance contact: director@restropilots.co.in`,
          `Security contact: security@restropilots.co.in`,
        ],
      },
    ],
  },

  {
    id: "dpa",
    title: "Data Processing Addendum",
    meta: `Version ${LEGAL_VERSION} · Effective when incorporated into an agreement with a Customer`,
    sections: [
      {
        heading: "1. Purpose and Scope",
        paragraphs: [
          `This Data Processing Addendum ("DPA") forms part of the agreement between Restropilots and the restaurant or business customer identified in the applicable subscription, order form or agreement ("Customer"). It applies to the extent Restropilots processes personal data on behalf of the Customer in providing the Services.`,
          `This DPA does not apply to processing for which Restropilots independently determines the purposes and means, including certain account administration, billing, platform security, legal compliance and direct service operations, except where applicable law provides otherwise.`,
        ],
      },
      {
        heading: "2. Definitions",
        paragraphs: [
          `"Applicable Data Protection Law" means data-protection and privacy law applicable to the relevant processing, including applicable provisions of Indian data-protection law as in force from time to time.`,
          `"Customer Personal Data" means personal data processed by Restropilots on behalf of the Customer under the agreement.`,
          `"Personal Data Breach" means a breach of security leading to accidental or unlawful destruction, loss, alteration, unauthorized disclosure of, or access to Customer Personal Data, as defined or recognized under applicable law.`,
          `"Subprocessor" means a third party engaged by Restropilots to process Customer Personal Data in connection with the Services.`,
        ],
      },
      {
        heading: "3. Roles and Instructions",
        paragraphs: [
          `The Customer determines the purposes for which it uses the Services and is responsible for the lawfulness of its instructions and for providing any notices or obtaining any permissions required for its collection and use of personal data.`,
          `Restropilots will process Customer Personal Data only to provide, maintain, secure and support the Services; comply with documented lawful instructions; and meet applicable legal obligations. The agreement, this DPA and the Customer's configuration and lawful use of the Services constitute documented instructions.`,
        ],
      },
      {
        heading: "4. Customer Obligations",
        bullets: [
          "Ensure that Customer Personal Data is collected and disclosed to Restropilots lawfully.",
          "Provide legally required notices and obtain required consents or other permissions.",
          "Use appropriate account and access controls.",
          "Avoid providing unnecessary or prohibited data.",
          "Respond to data-subject or data-principal requests for which the Customer is responsible.",
          "Notify Restropilots if an instruction is unlawful or materially changes the agreed processing.",
        ],
      },
      {
        heading: "5. Restropilots Obligations",
        paragraphs: [
          `Restropilots will process Customer Personal Data in accordance with this DPA, maintain appropriate safeguards, restrict personnel access based on need, require confidentiality from persons authorized to process the data, and provide reasonable assistance with applicable data-protection obligations taking into account the nature of the processing and information available to Restropilots.`,
        ],
      },
      {
        heading: "6. Security",
        paragraphs: [
          `Restropilots will maintain technical and organizational measures designed to protect Customer Personal Data against unauthorized access, loss, alteration and disclosure. The current measures are summarized in Annex B. Restropilots may update security measures as technology and risk evolve, provided the overall protection of Customer Personal Data is not materially reduced.`,
        ],
      },
      {
        heading: "7. Personal Data Breaches",
        paragraphs: [
          `Restropilots will maintain processes to identify and respond to security incidents. Upon becoming aware of a confirmed Personal Data Breach affecting Customer Personal Data, Restropilots will notify the Customer without undue delay where required by applicable law or the parties' agreement and provide information reasonably available to support the Customer's response.`,
          `Notification does not constitute an admission of fault or liability. The parties will cooperate regarding legally required notifications, containment, remediation and investigation.`,
        ],
      },
      {
        heading: "8. Data-Subject Requests",
        paragraphs: [
          `If Restropilots receives a request relating to Customer Personal Data for which the Customer is responsible, Restropilots may direct the requester to the Customer or provide reasonable assistance to the Customer, subject to the nature of the Services and applicable law.`,
        ],
      },
      {
        heading: "9. Subprocessors",
        paragraphs: [
          `The Customer authorizes Restropilots to use Subprocessors necessary to provide the Services. Restropilots will maintain appropriate contractual protections with Subprocessors to the extent required by applicable law and will remain responsible for its obligations under this DPA subject to the agreement.`,
          `The current known provider schedule is included in Annex C. Restropilots may replace or add providers as the Services evolve and should maintain an updated subprocessor register.`,
        ],
      },
      {
        heading: "10. Cross-Border Processing",
        paragraphs: [
          `Restropilots and its Subprocessors may process data in locations determined by their infrastructure and service configuration, subject to restrictions and requirements under applicable law. Restropilots will implement any legally required measures applicable to cross-border processing.`,
        ],
      },
      {
        heading: "11. Audit and Compliance Information",
        paragraphs: [
          `Upon reasonable written request and subject to confidentiality, security and proportionality requirements, Restropilots may provide information reasonably necessary to demonstrate compliance with this DPA. Audits should first rely on available documentation, certifications, questionnaires or reports. On-site audits, if legally required and not satisfied by other means, will be subject to reasonable scope, notice, confidentiality and non-disruption requirements.`,
        ],
      },
      {
        heading: "12. Return and Deletion",
        paragraphs: [
          `Following termination, the Customer will have the applicable export period described in the agreement, currently intended to be seven days. Thereafter, Restropilots will delete, anonymize or retain Customer Personal Data in accordance with its retention schedule, legal obligations, security needs and backup lifecycle. Data retained solely in backups will remain protected and will be removed through normal backup rotation unless restoration or retention is legally required.`,
        ],
      },
      {
        heading: "13. Liability and Order of Precedence",
        paragraphs: [
          `Liability under this DPA is subject to the limitations and exclusions in the main agreement unless applicable law requires otherwise. If this DPA conflicts with the main agreement on the processing of Customer Personal Data, this DPA controls to the extent of that conflict.`,
        ],
      },
      {
        heading: "Annex A — Processing Details",
        bullets: [
          "Subject matter: Provision of Restropilots restaurant SaaS and related technology services.",
          "Duration: For the term of the Customer relationship and applicable retention, transition and backup periods.",
          "Nature of processing: Collection, hosting, organization, storage, retrieval, transmission, analytics, support, security, deletion and other processing necessary to provide the Services.",
          "Purposes: Restaurant operations, QR ordering, table and order management, CRM, analytics, feedback, communications, payment support and related enabled functionality.",
          "Data subjects: Restaurant customers, restaurant personnel, users and other individuals whose personal data the Customer lawfully processes through the Services.",
          "Data categories: Names, contact information, account data, order history, table information, feedback, transaction identifiers, restaurant relationship data and other information submitted through enabled features.",
          "Frequency: Continuous or event-driven during use of the Services.",
        ],
      },
      {
        heading: "Annex B — Technical and Organizational Measures",
        bullets: [
          "HTTPS/TLS for production traffic.",
          "Password hashing rather than plaintext password storage.",
          "MFA for cloud administrative/root accounts.",
          "Separation of development and production environments.",
          "Restricted access to production databases and production systems.",
          "Encryption at rest where supported and configured.",
          "Automated backups.",
          "Secrets stored outside source code.",
          "Private source-code repositories.",
          "Code review before production deployment.",
          "Dependency and security scanning.",
          "Application, login, audit and security logging.",
          "Rate limiting and brute-force protections.",
          "Regular vulnerability scanning.",
          "Access based on job requirement and revocation when no longer required.",
          "Incident security contact and defined internal responsibility.",
        ],
        paragraphs: [
          `Security improvement items currently identified include formal written incident-response procedures, backup restoration testing, MFA for restaurant users, mature role-based access control for restaurant staff, and independent penetration testing.`,
        ],
      },
      {
        heading: "Annex C — Current Subprocessor / Service Provider Schedule",
        bullets: [
          "Netlify — Frontend hosting (website and technical request data). Verify production configuration.",
          "Render — Backend/API hosting (application and service data). Verify production region.",
          "MongoDB — Database services (application, restaurant and user data). Verify hosting entity and region.",
          "Amazon Web Services / S3 — Cloud and file/image storage (uploaded files, images and related data). Reported South Asia region; verify exact AWS region.",
          "Google services and APIs — Authentication and transactional services (identity, authentication and communication data). Depends on enabled Google service.",
          "Cashfree — Payments and subscriptions (payment and transaction information).",
          "GoDaddy-related email services — Marketing email (contact and communication data). Verify exact product/service.",
          "SMS provider — SMS communications (phone numbers and message data). Provider name to be inserted.",
          "Grafana — Monitoring/observability (metrics and operational data). Verify deployment model.",
          "Prometheus or related monitoring service — Metrics and monitoring (operational metrics). Verify exact provider/deployment.",
        ],
      },
    ],
  },

  {
    id: "security",
    title: "Security Overview",
    meta: `Version ${LEGAL_VERSION} · Public / Customer Security Summary`,
    sections: [
      {
        heading: "1. Security Commitment",
        paragraphs: [
          `Restropilots recognizes that restaurants and their customers rely on the confidentiality, integrity and availability of the platform. Our security program is designed to protect data through layered technical and organizational controls while the platform and security program continue to mature.`,
        ],
      },
      {
        heading: "2. Infrastructure and Environment Security",
        bullets: [
          "Production traffic is protected using HTTPS/TLS.",
          "Development and production environments are separated.",
          "Production database access is restricted and is not intended to be publicly accessible.",
          "Production access is limited based on job requirements.",
          "Cloud administrative/root accounts use MFA.",
          "Source-code repositories are private and secrets are stored outside source code.",
        ],
      },
      {
        heading: "3. Identity and Access Management",
        paragraphs: [
          `Restaurant accounts currently support email/password and OTP-based authentication, while customer authentication may use Google sign-in. Passwords are hashed rather than stored in plaintext. Administrative access is restricted and access is revoked when no longer required.`,
          `MFA for restaurant users and more granular role-based access controls for restaurant staff are identified as security roadmap items.`,
        ],
      },
      {
        heading: "4. Data Protection",
        paragraphs: [
          `Restropilots reports encryption at rest and HTTPS/TLS in transit. Automated backups are maintained. Backup restoration testing is an identified improvement item and should be completed on a recurring basis.`,
          `Restropilots does not intend to store full card numbers, CVV values or UPI IDs. Payment processing is supported through Cashfree, and Restropilots may retain transaction identifiers and status information.`,
        ],
      },
      {
        heading: "5. Secure Development",
        bullets: [
          "Code review before production deployment.",
          "Dependency and security scanning.",
          "Regular vulnerability scanning.",
          "Private code repositories.",
          "Separation of secrets from source code.",
          "Rate limiting and brute-force protections.",
        ],
      },
      {
        heading: "6. Logging and Monitoring",
        paragraphs: [
          `Restropilots maintains application, security, login and audit logging and uses monitoring/observability tooling that includes Grafana and Prometheus or related infrastructure. Current log retention has been reported as short and should be reviewed against operational, investigative and legal requirements.`,
        ],
      },
      {
        heading: "7. Incident Response",
        paragraphs: [
          `Restropilots has a defined internal security contact at security@restropilots.co.in. A formal written incident-response plan is an identified improvement item. The plan should define detection, triage, containment, evidence preservation, eradication, recovery, internal escalation, customer communication and legally required reporting.`,
        ],
      },
      {
        heading: "8. Vulnerability Management and Testing",
        paragraphs: [
          `Restropilots reports regular vulnerability scanning and dependency/security scanning. Independent penetration testing has not yet been performed and is an identified security maturity objective.`,
        ],
      },
      {
        heading: "9. Personnel and Access",
        paragraphs: [
          `At the time of preparation, two founders/directors have production access. Developers, freelancers and contractors are reported not to have production database or customer-data access. Access is intended to be restricted according to business need and revoked when a person no longer requires it.`,
        ],
      },
      {
        heading: "10. Third-Party Risk",
        paragraphs: [
          `Restropilots relies on third-party providers for infrastructure, hosting, database, cloud storage, authentication, payments, communications and monitoring. Restropilots should maintain an up-to-date vendor register and periodically review provider security, contractual and data-processing terms.`,
        ],
      },
      {
        heading: "11. Security Roadmap",
        bullets: [
          "Create and test a written incident-response plan.",
          "Test backup restoration and document recovery objectives.",
          "Define a formal data and log retention schedule.",
          "Introduce MFA for restaurant users where appropriate.",
          "Complete role-based access controls for restaurant staff.",
          "Conduct independent penetration testing.",
          "Maintain a verified subprocessor and cloud-region inventory.",
          "Document vulnerability remediation timelines and security review cadence.",
        ],
      },
      {
        heading: "12. Reporting Security Concerns",
        paragraphs: [
          `Security concerns may be reported to security@restropilots.co.in. Reporters should provide sufficient information to reproduce and assess the issue and should avoid accessing, modifying or retaining data beyond what is necessary to demonstrate the concern.`,
        ],
      },
    ],
  },

  {
    id: "nda",
    title: "One-Way Non-Disclosure Agreement",
    meta: `Template Version ${LEGAL_VERSION} · Restropilots as Disclosing Party`,
    sections: [
      {
        heading: "Parties",
        paragraphs: [
          `This Non-Disclosure Agreement ("Agreement") is entered into between Restropilots [insert exact MCA-registered legal name], having its registered office at OLD NO E-3-KH NO-305, NEW NO-144, East Delhi, Delhi 110096, India ("Disclosing Party"), and the Receiving Party identified in the applicable signed copy.`,
        ],
      },
      {
        heading: "1. Purpose",
        paragraphs: [
          `The Receiving Party may receive Confidential Information for the purpose of evaluating, discussing or performing a potential or actual business, employment, consultancy, development, agency, investment, vendor, partnership or other relationship with the Disclosing Party ("Purpose").`,
        ],
      },
      {
        heading: "2. Confidential Information",
        paragraphs: [
          `"Confidential Information" means non-public information disclosed directly or indirectly, in any form, that is identified as confidential or that a reasonable person would understand to be confidential given its nature and circumstances.`,
        ],
        bullets: [
          "Source code, object code, software, algorithms, models, APIs, architecture, databases and technical documentation.",
          "Product designs, roadmaps, research, prototypes, inventions, know-how and trade secrets.",
          "Security information, credentials, configurations, vulnerabilities, incident information and access methods.",
          "Pricing, financial information, forecasts, business plans, strategies and commercial terms.",
          "Customer, restaurant, partner, supplier and prospect information and lists.",
          "Restaurant data, customer data and other personal or confidential data.",
          "Marketing plans, analytics, operational information and internal processes.",
          "The existence and substance of confidential discussions, where the circumstances reasonably indicate confidentiality.",
        ],
      },
      {
        heading: "3. Exclusions",
        paragraphs: [
          `Confidential Information does not include information that the Receiving Party can demonstrate through contemporaneous records: (a) was lawfully known without restriction before disclosure; (b) becomes public through no breach of this Agreement; (c) is received lawfully from a third party without confidentiality obligation; or (d) is independently developed without use of the Confidential Information.`,
        ],
      },
      {
        heading: "4. Receiving Party Obligations",
        bullets: [
          "Use Confidential Information solely for the Purpose.",
          "Protect Confidential Information using at least reasonable care and no less care than it uses for its own information of similar sensitivity.",
          "Disclose Confidential Information only to personnel or professional advisers who need to know it for the Purpose and are bound by appropriate confidentiality obligations.",
          "Not copy, reverse engineer, exploit, publish, sell or disclose Confidential Information except as authorized in writing.",
          "Promptly notify the Disclosing Party of suspected unauthorized access, use or disclosure.",
          "Comply with reasonable security instructions relating to access to the Disclosing Party's systems or information.",
        ],
      },
      {
        heading: "5. Required Disclosure",
        paragraphs: [
          `If the Receiving Party is legally required to disclose Confidential Information, it may do so only to the extent legally required and, where legally permitted, will give prompt notice to the Disclosing Party and reasonable assistance in seeking protective treatment.`,
        ],
      },
      {
        heading: "6. Ownership and No Licence",
        paragraphs: [
          `All Confidential Information remains the property of the Disclosing Party or the relevant rights holder. No intellectual-property licence, assignment or ownership right is granted by disclosure except for the limited right to use Confidential Information for the Purpose.`,
          `This Agreement is a confidentiality agreement and, by itself, does not assign ownership of independently created work product, inventions or intellectual property. Any required assignment should be stated in a separate written agreement.`,
        ],
      },
      {
        heading: "7. Return or Destruction",
        paragraphs: [
          `Upon written request or termination of the relationship, the Receiving Party will promptly return or securely destroy Confidential Information, except for copies required by law or automatically retained in secure backups, which will remain subject to this Agreement until deleted through normal retention processes.`,
        ],
      },
      {
        heading: "8. Term and Survival",
        paragraphs: [
          `This Agreement begins on the Effective Date. The confidentiality and restricted-use obligations apply for five (5) years from each relevant disclosure. Trade secrets will remain protected for so long as they qualify for protection as trade secrets under applicable law.`,
        ],
      },
      {
        heading: "9. No Obligation to Proceed",
        paragraphs: [
          `Neither Party is obligated to enter into any further transaction or relationship. The Disclosing Party may stop discussions or access at any time, subject to existing contractual obligations.`,
        ],
      },
      {
        heading: "10. No Warranty",
        paragraphs: [
          `Unless otherwise agreed in writing, Confidential Information is provided for evaluation or the Purpose without representation or warranty as to completeness or accuracy. This clause does not exclude liability that cannot lawfully be excluded.`,
        ],
      },
      {
        heading: "11. Remedies",
        paragraphs: [
          `The Receiving Party acknowledges that unauthorized use or disclosure may cause harm for which monetary damages may be inadequate. The Disclosing Party may seek injunctive or equitable relief in addition to other remedies available under law.`,
        ],
      },
      {
        heading: "12. Governing Law and Disputes",
        paragraphs: [
          `This Agreement is governed by the laws of India. The Parties will first attempt in good faith to resolve disputes through negotiation. If unresolved within thirty days after written notice, the dispute may be referred to arbitration under the Arbitration and Conciliation Act, 1996, as amended, by a sole arbitrator mutually appointed by the Parties. The arbitration will be conducted in English, with New Delhi, India as the seat and venue.`,
          `Courts of competent jurisdiction at New Delhi, India will have jurisdiction over matters not capable of arbitration and permitted interim or enforcement relief.`,
        ],
      },
      {
        heading: "13. General",
        paragraphs: [
          `This Agreement constitutes the agreement between the Parties concerning confidentiality for the Purpose and supersedes prior discussions on that subject. Amendments must be in writing and agreed by the Parties. If any provision is unenforceable, the remaining provisions remain effective. A failure to enforce a provision is not a waiver.`,
        ],
      },
    ],
  },
];

export default legalDocuments;